import React from 'react';
import {
  Card,
  CardBody,
  Badge,
  Button,
  UncontrolledCollapse,
  Progress,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
  PartnerDocumentCompleteness,
  PartnerDocumentStatistics,
} from '../../../../types/documents';
import { formatCPF } from '../../../../utils/formatters';

interface Partner {
  id: string;
  name: string;
  cpf: string;
}

interface PartnerDocumentCardProps {
  partner: Partner;
  statistics: PartnerDocumentStatistics;
  completeness: PartnerDocumentCompleteness;
  onManageDocuments: (partnerId: string) => void;
}

const PartnerDocumentCard: React.FC<PartnerDocumentCardProps> = ({
  partner,
  statistics,
  completeness,
  onManageDocuments,
}) => {
  const { t } = useTranslation();

  // Calculate completion percentage
  const requiredDocs = 4; // RG, CNH, CPF, Proof of Address
  const completedDocs = [
    completeness.hasRG,
    completeness.hasCNH,
    completeness.hasCPF,
    completeness.hasProofOfAddress,
  ].filter(Boolean).length;
  const completionPercentage = (completedDocs / requiredDocs) * 100;

  // Color based on completion
  const getCompletionColor = () => {
    if (completionPercentage === 100) return 'success';
    if (completionPercentage >= 66) return 'warning';
    return 'danger';
  };

  return (
    <Card className="border">
      <CardBody>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="flex-grow-1">
            <h5 className="card-title mb-1">
              <i className="ri-user-line me-2"></i>
              {partner.name}
            </h5>
            <p className="text-muted mb-0">
              <small>CPF: {formatCPF(partner.cpf)}</small>
            </p>
          </div>
          <div className="text-end">
            {completeness.isComplete ? (
              <Badge color="success" className="mb-2">
                <i className="ri-checkbox-circle-line me-1"></i>
                {t('Completo')}
              </Badge>
            ) : (
              <Badge color="warning" className="mb-2">
                <i className="ri-error-warning-line me-1"></i>
                {t('Incompleto')}
              </Badge>
            )}
          </div>
        </div>

        {/* Completion Progress */}
        <div className="mb-3">
          <div className="d-flex justify-content-between mb-1">
            <small className="text-muted">{t('Documentos Obrigatórios')}</small>
            <small className="text-muted">
              {completedDocs}/{requiredDocs}
            </small>
          </div>
          <Progress
            value={completionPercentage}
            color={getCompletionColor()}
            style={{ height: '6px' }}
          />
        </div>

        {/* Required Documents Checklist */}
        <div className="mb-3">
          <Button
            color="link"
            id={`toggler-${partner.id}`}
            className="p-0 text-decoration-none"
          >
            <small>
              <i className="ri-arrow-down-s-line me-1"></i>
              {t('Ver Documentos Obrigatórios')}
            </small>
          </Button>
          <UncontrolledCollapse toggler={`#toggler-${partner.id}`}>
            <div className="mt-2 p-2 bg-light rounded">
              <div className="d-flex align-items-center mb-1">
                {completeness.hasRG ? (
                  <i className="ri-checkbox-circle-fill text-success me-2"></i>
                ) : (
                  <i className="ri-close-circle-fill text-danger me-2"></i>
                )}
                <small>{t('RG')}</small>
              </div>
              <div className="d-flex align-items-center mb-1">
                {completeness.hasCNH ? (
                  <i className="ri-checkbox-circle-fill text-success me-2"></i>
                ) : (
                  <i className="ri-close-circle-fill text-danger me-2"></i>
                )}
                <small>{t('CNH')}</small>
              </div>
              <div className="d-flex align-items-center mb-1">
                {completeness.hasCPF ? (
                  <i className="ri-checkbox-circle-fill text-success me-2"></i>
                ) : (
                  <i className="ri-close-circle-fill text-danger me-2"></i>
                )}
                <small>{t('CPF')}</small>
              </div>
              <div className="d-flex align-items-center mb-1">
                {completeness.hasProofOfAddress ? (
                  <i className="ri-checkbox-circle-fill text-success me-2"></i>
                ) : (
                  <i className="ri-close-circle-fill text-danger me-2"></i>
                )}
                <small>{t('Comprovante de Residência')}</small>
              </div>
              {completeness.hasECPF && (
                <div className="d-flex align-items-center">
                  <i className="ri-checkbox-circle-fill text-success me-2"></i>
                  <small>{t('e-CPF (Opcional)')}</small>
                </div>
              )}
            </div>
          </UncontrolledCollapse>
        </div>

        {/* Document Statistics */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex gap-3">
            <div>
              <div className="text-muted small">{t('Total')}</div>
              <div className="fw-medium">{statistics.totalDocuments}</div>
            </div>
            <div>
              <div className="text-success small">{t('Válidos')}</div>
              <div className="fw-medium text-success">
                {statistics.validDocuments}
              </div>
            </div>
            <div>
              <div className="text-danger small">{t('Pendentes')}</div>
              <div className="fw-medium text-danger">
                {statistics.pendingDocuments}
              </div>
            </div>
            {statistics.expiredDocuments > 0 && (
              <div>
                <div className="text-secondary small">{t('Expirados')}</div>
                <div className="fw-medium text-secondary">
                  {statistics.expiredDocuments}
                </div>
              </div>
            )}
            {statistics.expiringSoonDocuments > 0 && (
              <div>
                <div className="text-warning small">{t('Expirando')}</div>
                <div className="fw-medium text-warning">
                  {statistics.expiringSoonDocuments}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="d-grid">
          <Button
            color="primary"
            outline
            size="sm"
            onClick={() => onManageDocuments(partner.id)}
          >
            <i className="ri-file-list-line me-1"></i>
            {t('Gerenciar Documentos')}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

export default PartnerDocumentCard;
